import React from 'react';
import { makeStyles, InputBase } from '@material-ui/core';
import { useFormik } from 'formik';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import * as yup from 'yup';

import EditFooter from '@root/components/DocumentView/components/EditFooter';
import { checkAwayClick } from '@root/components/DocumentView/components/CommonEditor';
import { handleDocumentEditorTextSelect } from '@root/utils/file.utils';
import useTextSelect from '@root/utils/hooks/useTextSelect';

const encodeStr = (rawStr) => rawStr.replace(/[\u00A0-\u9999<>&]/gim, (i) => `&#${i.charCodeAt(0)};`);

const validationSchema = yup.object({
  headline: yup
    .string()
    .required('headline is required'),
  description: yup
    .string()
    .required('description is required'),
});

const useStyles = makeStyles(() => ({
  generationAtom: {
    padding: '12px',
  },

  SERPUrl: {
    color: 'rgb(32, 33, 36)',
    fontFamily: 'Arial',
    fontSize: '0.925rem',
  },
  SERPTitle: {
    color: 'rgb(26, 13, 171)',
    fontWeight: 400,
    fontlineHeight: '26px',
    fontSize: '20px',
    fontFamily: 'arial, sans-serif',
    marginBottom: '10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  SERPDescription: {
    fontWeight: 400,
    color: 'rgb(77, 81, 86)',
    fontFamily: 'arial, sans-serif',
    fontSize: '1rem',
    paddingBottom: '1.25rem',
  },
  paraStyle: {
    color: 'black',
    backgroundColor: 'rgb(240,240,240)',
    margin: '-2px',
    padding: '0 4px 0 4px',
    fontFamily: 'monospace',
    fontWeight: '400',
    fontSize: '13px',
  },
  customTextArea: {
    width: '100%',
    color: 'inherit',
    lineHeight: 'inherit',
    fontFamily: 'inherit',
    padding: '0',
    fontSize: 'inherit',
  },
}));

const SeoMetatagEditor = ({
  content: initialContent,
  onSave,
  onCancelEdit,
  children,
  fileData = {},
  onTextSelect,
}) => {
  const classes = useStyles();

  const headlineRef = React.useRef();
  const descriptionRef = React.useRef();

  const formik = useFormik({
    initialValues: {
      headline: initialContent[0],
      description: initialContent[1],
    },
    validationSchema,
    onSubmit: async (val) => {
      await onSave({ content: [val.headline, val.description] });
    },
  });

  const save = async () => {
    const newContent = [formik.values.headline, formik.values.description];
    await onSave({ content: newContent });
  };

  const onClickAway = async (e) => {
    e.preventDefault();

    if (!checkAwayClick(e.target)) {
      return;
    }

    const noSelection = window.getSelection().toString().length <= 0;
    if (noSelection) {
      await formik.submitForm();
      onCancelEdit();
    }
  };

  useTextSelect((selection) => {
    const fieldRefList = [{
      ref: headlineRef.current,
      field: 'headline',
    }, {
      ref: descriptionRef.current,
      field: 'description',
    }];

    handleDocumentEditorTextSelect({
      selection,
      fieldRefList,
      onTextSelect,
      content: formik.values,
      setFieldValue: formik.setFieldValue,
    });
  });

  let title = '';
  let description = '';
  if (initialContent.length >= 2) {
    title = initialContent[0] || '';
    description = initialContent[1] || '';
  }

  return (
    <form className={classes.root} onSubmit={formik.handleSubmit}>
      <ClickAwayListener
        onClickAway={onClickAway}
      >
        <div className={classes.generationAtom}>
          <div className={classes.SERPUrl}>
            {fileData.url}
          </div>

          <div
            className={classes.SERPTitle}
            ref={headlineRef}
          >
            <span>
              <InputBase
                multiline
                id="headline"
                name="headline"
                value={formik.values.headline}
                onChange={formik.handleChange}
                error={formik.touched.headline && Boolean(formik.errors.headline)}
                helperText={formik.touched.headline && formik.errors.headline}
                className={classes.customTextArea}
              />
            </span>
          </div>

          <div
            ref={descriptionRef}
            style={{ marginTop: 10 }}
          >
            <div className={classes.SERPDescription}>
              <InputBase
                multiline
                id="description"
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
                className={classes.customTextArea}
              />
            </div>
          </div>
          <div
            className={classes.paraStyle}
          >
            <p>
              {' '}
              {`<title>${encodeStr(title)}</title>`}
            </p>
            <p>
              {`<meta name="description" content="${encodeStr(
                description,
              )}">`}
            </p>
          </div>
        </div>

      </ClickAwayListener>
      {children}
      <EditFooter
        onCancelEdit={onCancelEdit}
        onSave={save}
      />
    </form>
  );
};

export default SeoMetatagEditor;
